export const HR_CALENDAR_PATH = '/hr/calendar'
/** Google OAuth dönüş adresi; Google Cloud Console'da "Authorized redirect URI" olarak kayıtlı olmalı */
export const GOOGLE_CALLBACK_PATH = '/hr/google/callback'

export type CalendarEventType = 'TASK' | 'EVENT' | 'INTERVIEW'

export type RecurrenceType =
  | 'NONE'
  | 'DAILY'
  | 'WEEKDAYS'
  | 'EVERY_N_DAYS'
  | 'WEEKLY_DAYS'
  | 'MONTHLY_DAY'
  | 'MONTHLY_LAST_DAY'
  | 'YEARLY'

/** CalendarOccurrenceDto: takvimde görünen tek bir tekrar */
export interface ICalendarOccurrence {
  eventId: string
  occurrenceDate: string
  title: string
  description?: string | null
  type: CalendarEventType
  start: string
  end: string
  allDay: boolean
  color?: string | null
  shared: boolean
  ownerUserName?: string | null
  /** Oturumdaki kullanıcı sahibi ve kayıt mülakat değil */
  editable: boolean
  completed: boolean
  recurrenceType: RecurrenceType
  recurrenceInterval?: number | null
  recurrenceWeekdays?: number[] | null
  recurrenceMonthDay?: number | null
  recurrenceMonth?: number | null
  recurrenceUntil?: string | null
  seriesStart: string
  seriesEnd: string
  interviewId?: string | null
  meetLink?: string | null
}

/** CalendarEventDao */
export interface ICalendarEventRequest {
  title: string
  description: string
  type: 'TASK' | 'EVENT'
  start: string
  end: string
  allDay: boolean
  color: string
  shared: boolean
  recurrenceType: RecurrenceType
  recurrenceInterval?: number | null
  recurrenceWeekdays?: number[] | null
  recurrenceMonthDay?: number | null
  recurrenceMonth?: number | null
  recurrenceUntil?: string | null
}

export const RECURRENCE_OPTIONS: {value: RecurrenceType; label: string}[] = [
  {value: 'NONE', label: 'Tekrar yok'},
  {value: 'DAILY', label: 'Her gün'},
  {value: 'WEEKDAYS', label: 'Hafta içi her gün (Pazartesi - Cuma)'},
  {value: 'EVERY_N_DAYS', label: 'X günde bir'},
  {value: 'WEEKLY_DAYS', label: 'Haftanın belirli günleri'},
  {value: 'MONTHLY_DAY', label: 'Her ayın X. günü'},
  {value: 'MONTHLY_LAST_DAY', label: 'Her ayın son günü'},
  {value: 'YEARLY', label: 'Her yıl belirli bir gün'},
]

/** ISO gün numarası: 1 = Pazartesi ... 7 = Pazar */
export const WEEKDAYS: {value: number; short: string; label: string}[] = [
  {value: 1, short: 'Pzt', label: 'Pazartesi'},
  {value: 2, short: 'Sal', label: 'Salı'},
  {value: 3, short: 'Çar', label: 'Çarşamba'},
  {value: 4, short: 'Per', label: 'Perşembe'},
  {value: 5, short: 'Cum', label: 'Cuma'},
  {value: 6, short: 'Cmt', label: 'Cumartesi'},
  {value: 7, short: 'Paz', label: 'Pazar'},
]

export const MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
]

export const EVENT_COLORS = ['#009ef7', '#50cd89', '#ffc700', '#f1416c', '#7239ea', '#3f4254']

export const TYPE_LABEL: Record<CalendarEventType, string> = {
  TASK: 'Görev',
  EVENT: 'Etkinlik',
  INTERVIEW: 'Mülakat',
}

/** Tekrar kuralının okunabilir özeti: "Her ayın 15. günü", "2 haftada bir Pzt, Per" */
export function recurrenceSummary(rule: {
  recurrenceType: RecurrenceType
  recurrenceInterval?: number | null
  recurrenceWeekdays?: number[] | null
  recurrenceMonthDay?: number | null
  recurrenceMonth?: number | null
  recurrenceUntil?: string | null
}): string {
  let text: string
  switch (rule.recurrenceType) {
    case 'DAILY':
      text = 'Her gün'
      break
    case 'WEEKDAYS':
      text = 'Hafta içi her gün'
      break
    case 'EVERY_N_DAYS':
      text = `${rule.recurrenceInterval ?? 1} günde bir`
      break
    case 'WEEKLY_DAYS': {
      const days = (rule.recurrenceWeekdays || [])
        .map((day) => WEEKDAYS.find((item) => item.value === day)?.label)
        .filter(Boolean)
        .join(', ')
      const interval = rule.recurrenceInterval && rule.recurrenceInterval > 1 ? `${rule.recurrenceInterval} haftada bir ` : 'Her hafta '
      text = `${interval}${days}`
      break
    }
    case 'MONTHLY_DAY':
      text = `Her ayın ${rule.recurrenceMonthDay}. günü`
      break
    case 'MONTHLY_LAST_DAY':
      text = 'Her ayın son günü'
      break
    case 'YEARLY':
      text = `Her yıl ${rule.recurrenceMonthDay} ${MONTHS[(rule.recurrenceMonth ?? 1) - 1]}`
      break
    default:
      return 'Tekrar yok'
  }
  if (rule.recurrenceUntil) {
    text += ` · ${new Date(`${rule.recurrenceUntil}T00:00:00`).toLocaleDateString('tr-TR')} tarihine kadar`
  }
  return text
}

const pad = (value: number) => String(value).padStart(2, '0')

/** Backend LocalDateTime biçimi (saat dilimi yok): 2026-10-08T14:00:00 */
export const toLocalIso = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}:00`

export const toDateOnly = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** "2026-10-08T14:00:00" → ["2026-10-08", "14:00"] */
export const splitDateTime = (value: string): [string, string] => [value.substring(0, 10), value.substring(11, 16)]

export const formatTime = (value: string) => value.substring(11, 16)
